import { addApolloState, ApolloError, initializeApollo } from '@sortlist-frontend/data-fetching/public';
import { GetAgencyProfileQuery } from '@sortlist-frontend/data-fetching/public-types';
import { MediaAttachment } from '@sortlist-frontend/media';
import { Monitor } from '@sortlist-frontend/mlm/server';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import {
  assertNonEmptyString,
  HttpBadRequest,
  HttpNotFound,
  isAutomation,
  isAutomationUserAgent,
  isPlainObject,
} from '@sortlist-frontend/utils';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { Fragment, useRef } from 'react';

import { DomainInfo } from '_backend/integration/domain-info';
import { getAgencyAlternates } from '_components/AlternateLinks/utils';
import { PageHead } from '_components/base/head';
import { PublicGoogleTagManager } from '_components/common';
import { Public404Page } from '_components/error/public-404-page';
import { LayoutPagesRouter } from '_components/layout/LayoutPagesRouter';
import { AgencyProfileJsonLd, PageSeoTag } from '_components/SEO';
import { AGENCY_SEO_QUERY } from '_components/SEO/hooks/useGetAgency';
import { cacheConfig } from '_config/cache.config';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { isSerializedError, SerializedError, serializeError } from '_core/error/error-serializer';
import { useExpertisesWithoutContext } from '_core/repos/expertises.repo';
import * as publicApi from '_core/repos/public-api.repo';
import { pagesRepo, searchPageCacheKey } from '_core/repos/public-api/page.repo';
import { fetchWithCache } from '_core/utils/backend-cache/fetch-with-cache';
import { baseSsrPrefetchQueries, BaseSsrPrefetchQueriesResponse } from '_core/utils/public-api';
import { getServerSideTranslations } from '_core/utils/ssr';
import { AgencyProfilePage } from '_features/AgencyProfile/AgencyProfile';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { AGENCY_AWARDS_QUERY, AWARDS_PER_PAGE } from '_features/AgencyProfile/hooks/useGetAgencyAwards';
import { AGENCY_PROFILE_QUERY } from '_features/AgencyProfile/hooks/useGetAgencyProfile';
import { AGENCY_REVIEWS_QUERY } from '_features/AgencyProfile/hooks/useGetAgencyReviews';
import { servicePagesCacheKey } from '_features/AgencyProfile/hooks/useServicePages';
import { getSeoPageImage, getSeoPageTitle } from '_features/AgencyProfile/utils';

import { acceptLanguageHeader } from '../../core/api/api.utils';

const { PublicApiRepo } = publicApi;
const DOMAIN_CONFIG_ERROR_STATUSES = [400, 404, 410];

type Props = {
  locale?: string;
  canonical: string;
  origin?: string;
  agency: GetAgencyProfileQuery['agency'] | null;
  error: SerializedError | null;
  disableCache: boolean;
  top: string[] | string | null;
  publicApiNavBarUrl?: string;
  dehydratedState?: DehydratedState;
  isAutomationRequest?: boolean;
};

export default function PageRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { locale = 'en', agency } = props;
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const scrollableCtn = useRef<HTMLDivElement>(null);
  const domainInfo = DomainInfo.getFromOrigin(props?.origin ?? '');
  const { data: expertisesData } = useExpertisesWithoutContext(domainInfo?.getOriginUrl() ?? '', locale);

  if (props == null) return;

  const { error, canonical, origin, top, publicApiNavBarUrl = '', isAutomationRequest } = props;

  // Check if user agent is sortlist-automation (works both SSR and client-side)
  const shouldSkipJsonLd = isAutomationRequest || isAutomation();

  if (error && isSerializedError(error)) {
    const { status } = error;
    if (DOMAIN_CONFIG_ERROR_STATUSES.includes(status as number))
      return <Public404Page message={t('common:pageErrors.notFound.agencyNotFound')} locale={locale} />;
  }

  const agencySlug = agency?.slug as string;
  const agencyUuid = agency?.uuid as string;

  PublicApiRepo.publicApiNavBarUrl = publicApiNavBarUrl;

  const firstServiceExpertiseId =
    expertisesData?.find((e) => e.uuid === agency?.services.nodes?.[0]?.expertise?.uuid)?.expertiseId ?? null;
  return (
    <Fragment>
      <PageHead canonical={canonical} origin={origin} />
      {/* For some reason if I add the alternates in the PageHead component only 7 finish in the dom, really weird */}
      <Head>
        {getAgencyAlternates(agencySlug, origin as string, locale).map(({ key, href, hreflang }) => {
          return <link key={key} rel="alternate" href={href} hrefLang={hreflang} />;
        }) ?? null}
      </Head>
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin || ''}
        locale={locale}
        pageData={{ agencySlug, agencyUuid }}
        briefingOptions={{
          page: 'agency-profile',
          address: agency?.mainOffice?.locality?.country?.name ?? undefined,
          placeId: agency?.mainOffice?.locality?.googlePlaceId ?? undefined,
          expertise: firstServiceExpertiseId as number,
          iso31661: agency?.mainOffice?.locality?.country?.iso31661 ?? undefined,
        }}>
        <PageSeoTag
          canonical={canonical}
          title={getSeoPageTitle(
            top,
            agency?.name ?? '',
            agency?.mainOffice?.locality?.name ?? '',
            agency?.reviews?.totalCount ?? 0,
            t,
          )}
          description={t('agency:profile.seo.metaDescription', { agencyName: agency?.name })}
          image={getSeoPageImage(
            top,
            agency?.coverPictureUrl ? ({ url: agency.coverPictureUrl } as unknown as MediaAttachment) : null,
          )}
        />
        {!shouldSkipJsonLd && <AgencyProfileJsonLd slug={agencySlug} canonical={canonical} />}
        <PublicGoogleTagManager />

        <LayoutPagesRouter scrollableCtn={scrollableCtn}>
          <AgencyProfilePage scrollableCtn={scrollableCtn} top={top} />
        </LayoutPagesRouter>
      </PublicAppContextProvider>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const { disableCache: disableCacheParam, top = null } = context.query;
  const { i18nNamespaces } = agencyProfileConfig;
  const agencySlug = context.params?.slug as string;
  let error: SerializedError | null = null;
  const disableCache = disableCacheParam === 'true';

  let domainRelevantInfo;
  try {
    domainRelevantInfo = extractDomainRelevantInfoFromHeaders(context.req);
  } catch (e) {
    // @TODO: use a more elegant way when upgrade to NextJS 10 https://nextjs.org/blog/next-10#redirect-and-notfound-support-for-getstaticprops--getserversideprops
    context.res.statusCode = (e as any).status;
    error = serializeError(e as Error);

    return {
      props: {
        error,
        disableCache,
        canonical: '',
        agencySlug,
        agencyUuid: '',
        top,
        ...(await getServerSideTranslations('en', i18nNamespaces)),
      },
    };
  }

  const { canonical, origin, locale } = domainRelevantInfo;

  assertNonEmptyString(origin, new HttpBadRequest({ message: 'Missing [origin] parameter' }));
  assertNonEmptyString(agencySlug, new HttpBadRequest({ message: 'Missing [agencySlug] parameter' }));

  const queryClient = new QueryClient();
  let agencyProfileResponse = null;
  let agencyProfile: GetAgencyProfileQuery['agency'] | null = null;
  let response: BaseSsrPrefetchQueriesResponse | null = null;

  const apolloClient = initializeApollo({ schema: 'public' });

  try {
    [agencyProfileResponse, , , response] = await Promise.all([
      apolloClient.query({
        query: AGENCY_PROFILE_QUERY,
        variables: {
          slug: agencySlug,
        },
        context: {
          headers: {
            'Accept-Language': acceptLanguageHeader(locale),
          },
        },
      }),
      apolloClient.query({
        query: AGENCY_REVIEWS_QUERY,
        variables: {
          slug: agencySlug,
          first: 100,
        },
        context: {
          headers: {
            'Accept-Language': acceptLanguageHeader(locale),
          },
        },
      }),
      apolloClient.query({
        query: AGENCY_AWARDS_QUERY,
        variables: {
          slug: agencySlug,
          first: AWARDS_PER_PAGE,
        },
        context: {
          headers: {
            'Accept-Language': acceptLanguageHeader(locale),
          },
        },
      }),
      baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient),
    ]);
    agencyProfile = agencyProfileResponse.data.agency;
    if (agencyProfile != null) {
      const expertises =
        agencyProfile?.services?.nodes
          ?.map((service) => service?.expertise)
          .filter((expertise): expertise is NonNullable<typeof expertise> => expertise != null) ?? [];
      const localeId = response.domain?.availableLocales.find((domainLocale) => domainLocale.code === locale)
        ?.id as string;
      const countryId = response.domain?.location?.id as string;
      const pages = await Promise.all(
        expertises?.map(async (expertise) => {
          const params = {
            host: response?.domain?.host as string,
            associatedTopics: [localeId, expertise.id ?? '', countryId],
            locale: localeId,
          };
          const page = await fetchWithCache({
            key: searchPageCacheKey(params),
            fetchFunction: () => pagesRepo.getPages(params, locale, origin),
            options: { ttlSeconds: 3600 * 24 * 7, optionalValue: true }, // 7 days
          });

          return {
            expertiseSlug: expertise?.slug,
            page: page ?? null,
          };
        }) ?? [],
      );

      await queryClient.prefetchQuery({
        queryKey: servicePagesCacheKey(agencySlug, locale, origin),
        queryFn: () => pages,
      });
    }
  } catch (e) {
    if ((e as ApolloError).message === 'Agency not found') {
      // Expected — handled below via agencyProfile == null check
    } else {
      Monitor.captureException(e instanceof Error ? e : new Error('Unexpected SSR error in agency page'), {
        extra: { slug: agencySlug },
        tags: { context: 'getServerSideProps' },
      });
      throw e;
    }
  }

  if (agencyProfile == null || agencyProfile?.live === false) {
    context.res.statusCode = 404;
    error = serializeError(new HttpNotFound({ message: 'This agency was not found' }));
  }

  const cacheOptions = cacheConfig.pages['agencyProfile'];

  // Enable edge caching
  if (!context.res.headersSent && agencyProfile != null && isPlainObject(cacheOptions) && !disableCache) {
    try {
      context.res.setHeader('Cache-Control', cacheOptions.httpCacheControl);
      context.res.setHeader('Vary', cacheOptions?.httpVary?.join(',') ?? '');
      context.res.setHeader('X-Cache-Tag', agencySlug);
    } catch {
      // too dangerous to not discard error here
      // in case of res.end() was called and headerSent is not trustable.
      // @todo find a better way to handle redirects before with Nextjs and
      //       required props
    }
  }

  const userAgent = context.req.headers['user-agent'] ?? '';
  const isAutomationRequest = isAutomationUserAgent(userAgent);

  // Dont build JSONLD for automated requests as their profiles can be deleted
  // and skip if there's an error
  if (!isAutomationRequest && error == null) {
    try {
      await apolloClient.query({
        query: AGENCY_SEO_QUERY,
        variables: {
          slug: agencySlug,
        },
        context: {
          headers: {
            'Accept-Language': acceptLanguageHeader(locale),
          },
        },
      });
    } catch (e) {
      Monitor.captureException(e instanceof Error ? e : new Error('SSR agency SEO query failed'), {
        extra: { slug: agencySlug },
        tags: { context: 'getServerSideProps' },
      });
    }
  }

  return addApolloState(apolloClient, {
    props: {
      locale,
      canonical,
      origin,
      agency: agencyProfile,
      publicApiNavBarUrl: response?.publicApiNavBarUrl ?? '',
      dehydratedState: dehydrate(queryClient),
      error,
      disableCache,
      top,
      isAutomationRequest,
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  });
};
